/**
 * authIdentity.js — auth Firebase LEGATA ALL'IDENTITÀ (Opzione 2).
 *
 * Sostituisce `signInAnonymously` ai punti in cui un'identità è stabilita
 * (claim, sblocco, recover, restore). L'uid Firebase risultante è
 * DETERMINISTICO: uid = deriveUid(pubKeySign) → ogni identità ha sempre il suo
 * uid, mai condiviso con un'altra sullo stesso device → identità scollegabili.
 *
 * Flusso: firmiamo `nullum-auth:v1:<pubB64>:<ts>` con la chiave dell'identità,
 * la function `mintAuthToken` verifica la firma e conia un custom token, poi
 * `signInWithCustomToken`.
 */

import { auth, callFn, signInWithCustomToken } from './firebase';
import { sign, toBase64Url } from './crypto';

const AUTH_PREFIX = 'nullum-auth:v1:';

/**
 * Autentica Firebase come l'identità data.
 * @param {{ signPub: Uint8Array, signSec: Uint8Array }} keys chiavi di firma Ed25519
 * @returns {Promise<string>} l'uid Firebase (== deriveUid(signPub))
 */
export async function signInAsIdentity({ signPub, signSec }) {
  if (!(signPub instanceof Uint8Array) || !(signSec instanceof Uint8Array)) {
    throw new Error('AUTH_KEYS_INVALID');
  }
  const pubB64 = await toBase64Url(signPub);
  const ts = Date.now();
  const msg = AUTH_PREFIX + pubB64 + ':' + ts;
  const sigBytes = await sign(msg, signSec);
  const sigB64 = await toBase64Url(sigBytes);

  let res;
  try {
    res = await callFn('mintAuthToken')({ pub: pubB64, ts, sig: sigB64 });
  } catch (e) {
    console.warn('[AUTHID] mintAuthToken ERR code=' + (e?.code || '?') + ' msg=' + (e?.message || e));
    throw e;
  }
  const token = res?.data?.token;
  if (!token) throw new Error('AUTH_NO_TOKEN');
  try {
    await signInWithCustomToken(auth(), token);
  } catch (e) {
    console.warn('[AUTHID] signInWithCustomToken ERR code=' + (e?.code || '?') + ' msg=' + (e?.message || e));
    throw e;
  }
  return res.data.uid;
}
